'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, UserRole, ImpersonationInfo } from '@/services/authService';

// ============================================
// AUTH CONTEXT
// ============================================
interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, totpToken?: string) => Promise<User>;
  register: (email: string, password: string, name: string, role?: UserRole, extra?: {
    company_name?: string;
    company_vat?: string;
    company_pec?: string;
    company_sdi?: string;
    vendor_phone?: string;
    invitation_token?: string;
    invite_code?: string;
    referral_source?: string;
    [k: string]: string | boolean | undefined | null;
  }) => Promise<User>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isGuest: boolean;
  isHost: boolean;
  isVendor: boolean;
  isCompany: boolean;
  isAdmin: boolean;
  isServiceProvider: boolean;
  isOwnerRole: boolean;
  // Cambio utente / impersonazione
  impersonating: ImpersonationInfo | null;
  /** Banner impersonazione visibile: impersonando E non chiuso dall'utente. Si riapre a ogni nuovo impersonamento/login. */
  showImpersonationBanner: boolean;
  /** Chiude il banner impersonazione per la sessione corrente (finché non cambia l'identità impersonata). */
  dismissImpersonationBanner: () => void;
  startImpersonate: (targetUserId: string) => Promise<void>;
  stopImpersonate: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [impersonating, setImpersonating] = useState<ImpersonationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Setting globale (admin): banner impersonazione attivo? Default OFF → nessun banner.
  const [bannerSettingEnabled, setBannerSettingEnabled] = useState(false);

  // Chiave identità impersonazione (admin → utente target): quando cambia (nuovo
  // impersonamento) il banner si riapre. Null quando non si sta impersonando.
  const impersonationKey = impersonating ? `${impersonating.admin_email || ''}->${user?.id || ''}` : null;
  useEffect(() => { setBannerDismissed(false); }, [impersonationKey]);

  // Legge il setting solo quando serve (si sta impersonando), best-effort.
  useEffect(() => {
    if (!impersonating) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/api/settings/public/impersonation-banner', { cache: 'no-store' });
        const j = await r.json();
        if (alive) setBannerSettingEnabled(!!j?.data?.enabled);
      } catch { /* default OFF */ }
    })();
    return () => { alive = false; };
  }, [impersonating]);

  const refreshAuth = async () => {
    try {
      const me = await authService.getMe();
      if (me.user?.is_suspended) {
        await authService.logout();
        setUser(null);
        setImpersonating(null);
        return;
      }
      setUser(me.user);
      setImpersonating(me.impersonating);
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      setImpersonating(null);
    }
  };

  useEffect(() => {
    (async () => {
      await refreshAuth();
      setLoading(false);
    })();
  }, []);

  const login = async (email: string, password: string, totpToken?: string) => {
    const { user } = await authService.login(email, password, totpToken);
    setUser(user);
    return user;
  };

  const register = async (email: string, password: string, name: string, role: UserRole = 'CUSTOMER', extra?: {
    company_name?: string;
    company_vat?: string;
    company_pec?: string;
    company_sdi?: string;
    vendor_phone?: string;
    invitation_token?: string;
    invite_code?: string;
    referral_source?: string;
    [k: string]: string | boolean | undefined | null;
  }) => {
    const { user } = await authService.register(email, password, name, role, extra);
    setUser(user);
    return user;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setImpersonating(null);
    // Dopo logout dalla dashboard admin (path-based) → home.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      window.location.href = '/';
    }
  };

  const startImpersonate = async (targetUserId: string) => {
    await authService.startImpersonate(targetUserId);
    // Hard reload per aggiornare tutto il contesto (cookie, dashboard, ecc.).
    // Routing path-based: atterro su /partner, che instrada al dashboard del
    // ruolo target (vendor/excursion). Relative → funziona su qualsiasi host.
    if (typeof window !== 'undefined') {
      window.location.href = '/partner';
    }
  };

  const stopImpersonate = async () => {
    await authService.stopImpersonate();
    if (typeof window !== 'undefined') {
      // Torna alla dashboard admin (path-based)
      window.location.href = '/admin';
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isGuest: user?.role === 'CUSTOMER',
    isHost: user?.role === 'ADMIN', // backward compat: isHost now maps to ADMIN
    isVendor: user?.role === 'VENDOR',
    isCompany: user?.role === 'COMPANY',
    isAdmin: user?.role === 'ADMIN',
    isServiceProvider: user?.role === 'SERVICE_PROVIDER',
    isOwnerRole: ['VENDOR', 'COMPANY', 'ADMIN'].includes(user?.role || ''),
    impersonating,
    showImpersonationBanner: !!impersonating && bannerSettingEnabled && !bannerDismissed,
    dismissImpersonationBanner: () => setBannerDismissed(true),
    startImpersonate,
    stopImpersonate,
    refresh: refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ============================================
// MAINTENANCE CONTEXT
// ============================================
interface MaintenanceContextType {
  isMaintenanceMode: boolean;
  maintenanceLoading: boolean;
  isDevAuthenticated: boolean;
  handleDevLogin: () => void;
  handleExitDevMode: () => void;
}

const MaintenanceContext = createContext<MaintenanceContextType | null>(null);

interface MaintenanceProviderProps {
  children: ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
  const [isDevAuthenticated, setIsDevAuthenticated] = useState(false);
  // Start with maintenance OFF to avoid flash — DB fetch will correct if needed
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  useEffect(() => {
    // If dev already authenticated via sessionStorage, skip the flash
    if (typeof window !== 'undefined' && sessionStorage.getItem('devAuthenticated') === 'true') {
      setIsDevAuthenticated(true);
      setMaintenanceLoading(false);
      return;
    }

    // Fetch live maintenance status from DB
    const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api';
    fetch(`${API_URL}/settings/public/maintenance`)
      .then(r => r.json())
      .then(data => {
        if (data?.success) {
          setIsMaintenanceMode(data.data.enabled);
        }
      })
      .catch(() => {
        // Fallback to env variable on error
        setIsMaintenanceMode(process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true');
      })
      .finally(() => setMaintenanceLoading(false));
  }, []);

  useEffect(() => {
    if (isMaintenanceMode && typeof window !== 'undefined') {
      const authenticated = sessionStorage.getItem('devAuthenticated');
      if (authenticated === 'true') {
        setIsDevAuthenticated(true);
      }
    }
  }, [isMaintenanceMode]);

  const handleDevLogin = () => {
    sessionStorage.setItem('devAuthenticated', 'true');
    setIsDevAuthenticated(true);
  };

  const handleExitDevMode = () => {
    sessionStorage.removeItem('devAuthenticated');
    setIsDevAuthenticated(false);
  };

  return (
    <MaintenanceContext.Provider value={{
      isMaintenanceMode,
      maintenanceLoading,
      isDevAuthenticated,
      handleDevLogin,
      handleExitDevMode
    }}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = (): MaintenanceContextType => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenance must be used within MaintenanceProvider');
  }
  return context;
};

export default AuthProvider;
